import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Plus, IndianRupee, CheckCircle2, MessageCircle, Share2, Copy, Check, Mail } from "lucide-react";
import { toast } from "sonner";

const CLASSES = ["Playgroup", "Nursery", "LKG", "UKG"];
const MODES = ["Cash", "UPI", "Bank Transfer"];

export default function Fees() {
  const [items, setItems] = useState([]);
  const [children, setChildren] = useState([]);
  const [classFilter, setClassFilter] = useState("");
  const [status, setStatus] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(null);
  const [payMode, setPayMode] = useState("UPI");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderData, setReminderData] = useState(null);
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [form, setForm] = useState({
    child_id: "", title: "Term Fees", amount: 0,
    due_date: new Date().toISOString().slice(0, 10), paid: false, notes: "",
  });

  const load = async () => {
    const params = {};
    if (classFilter) params.class_name = classFilter;
    if (status) params.status = status;
    const [f, c] = await Promise.all([api.get("/fees", { params }), api.get("/children")]);
    setItems(f.data);
    setChildren(c.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [classFilter, status]);

  const today = new Date().toISOString().slice(0, 10);
  const statusOf = (f) => {
    if (f.paid) return "paid";
    if (f.due_date < today) return "overdue";
    return "unpaid";
  };
  const chipFor = { paid: "chip-green", overdue: "chip-pink", unpaid: "chip-amber" };
  const labelFor = { paid: "Paid", overdue: "Overdue", unpaid: "Due" };

  const save = async () => {
    try {
      await api.post("/fees", { ...form, amount: Number(form.amount) });
      toast.success("Fee added");
      setAddOpen(false);
      load();
    } catch { toast.error("Failed"); }
  };
  const markPaid = async () => {
    try {
      await api.post(`/fees/${payOpen.id}/mark-paid`, { payment_date: payDate, payment_mode: payMode });
      toast.success("Marked paid");
      setPayOpen(null);
      load();
    } catch { toast.error("Failed"); }
  };

  const totalDue = items.filter((f) => !f.paid).reduce((s, f) => s + f.amount, 0);
  const totalCollected = items.filter((f) => f.paid).reduce((s, f) => s + f.amount, 0);

  const openReminders = async () => {
    setLoadingReminders(true);
    setReminderOpen(true);
    try {
      const { data } = await api.post("/fees/bulk-reminder");
      setReminderData(data);
    } catch {
      toast.error("Could not fetch reminders");
      setReminderOpen(false);
    } finally {
      setLoadingReminders(false);
    }
  };

  const openAllReminders = () => {
    (reminderData?.recipients || []).forEach((r, i) => {
      setTimeout(() => window.open(r.wa_link, "_blank", "noopener"), i * 250);
    });
  };

  const emailAllReminders = async () => {
    try {
      const { data } = await api.post("/emails/bulk-fee-reminder");
      const bits = [];
      if (data.sent) bits.push(`${data.sent} sent`);
      if (data.skipped_no_email) bits.push(`${data.skipped_no_email} skipped (no email)`);
      if (data.failed) bits.push(`${data.failed} failed`);
      toast.success(bits.join(" · ") || "Nothing to send");
    } catch {
      toast.error("Bulk email failed");
    }
  };

  const emailOne = async (child_id, child_name) => {
    try {
      await api.post(`/emails/fee-reminder/${child_id}`);
      toast.success(`Email sent to ${child_name}'s parent`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email failed");
    }
  };

  const copyOne = async (idx, msg) => {
    try {
      await navigator.clipboard.writeText(msg);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1600);
    } catch {}
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black">Fee Management</h1>
          <p className="text-muted-foreground font-medium">Track dues, mark payments, keep the little school running.</p>
        </div>
        <Button data-testid="add-fee-btn" onClick={() => setAddOpen(true)} className="rounded-full h-12 px-6 font-bold hover:-translate-y-0.5 hover:shadow-md transition-all">
          <Plus size={18} /> Add Fee
        </Button>
      </header>

      <div className="flex flex-wrap gap-3">
        <Button
          data-testid="send-reminders-btn"
          onClick={openReminders}
          variant="outline"
          className="rounded-full h-11 px-5 font-bold border-primary/40 text-primary hover:bg-primary/10"
        >
          <MessageCircle size={16} /> Send Overdue Reminders on WhatsApp
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="card-soft rounded-3xl p-6">
          <div className="text-xs uppercase font-bold tracking-widest text-muted-foreground">Collected</div>
          <div className="text-3xl md:text-4xl font-black mt-1">₹{totalCollected.toLocaleString()}</div>
        </div>
        <div className="card-soft rounded-3xl p-6">
          <div className="text-xs uppercase font-bold tracking-widest text-muted-foreground">Outstanding</div>
          <div className="text-3xl md:text-4xl font-black mt-1 text-destructive/90">₹{totalDue.toLocaleString()}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Select value={classFilter || "__all"} onValueChange={(v) => setClassFilter(v === "__all" ? "" : v)}>
          <SelectTrigger className="h-11 rounded-full w-48"><SelectValue placeholder="All classes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All classes</SelectItem>
            {CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          {[["", "All"], ["Paid", "Paid"], ["Unpaid", "Unpaid"]].map(([v, l]) => (
            <button key={l} onClick={() => setStatus(v)}
              className={`px-4 py-2 rounded-full font-semibold text-sm ${status === v ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {items.map((f) => {
          const s = statusOf(f);
          return (
            <div key={f.id} data-testid={`fee-row-${f.id}`} className="card-soft rounded-2xl p-4 flex flex-wrap items-center gap-3">
              <div className={`w-12 h-12 rounded-xl ${chipFor[s]} flex items-center justify-center`}>
                <IndianRupee size={18} />
              </div>
              <div className="flex-1 min-w-[180px]">
                <div className="font-extrabold">{f.child_name} <span className="text-muted-foreground font-normal text-xs">· {f.class_name}</span></div>
                <div className="text-sm text-muted-foreground">{f.title} · Due {f.due_date}</div>
              </div>
              <div className="font-black text-lg">₹{f.amount.toLocaleString()}</div>
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${chipFor[s]}`}>{labelFor[s]}</span>
              {!f.paid && (
                <Button data-testid={`mark-paid-${f.id}`} size="sm" className="rounded-full" onClick={() => setPayOpen(f)}>
                  <CheckCircle2 size={14} /> Mark Paid
                </Button>
              )}
              {f.paid && <span className="text-xs text-muted-foreground font-semibold">{f.payment_mode} · {f.payment_date}</span>}
            </div>
          );
        })}
        {items.length === 0 && <div className="text-center text-muted-foreground py-12">No fees found.</div>}
      </div>

      {/* Add fee */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle>Add Fee</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Child</Label>
              <Select value={form.child_id} onValueChange={(v) => setForm({ ...form, child_id: v })}>
                <SelectTrigger data-testid="fee-child" className="h-11 rounded-xl"><SelectValue placeholder="Select child" /></SelectTrigger>
                <SelectContent>
                  {children.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.class_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-11 rounded-xl" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount (₹)</Label><Input data-testid="fee-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="h-11 rounded-xl" /></div>
              <div><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="h-11 rounded-xl" /></div>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-fee" onClick={save} className="rounded-full">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark paid */}
      <Dialog open={!!payOpen} onOpenChange={(v) => !v && setPayOpen(null)}>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle>Mark as Paid</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Payment Date</Label>
              <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className="h-11 rounded-xl" />
            </div>
            <div>
              <Label>Payment Mode</Label>
              <Select value={payMode} onValueChange={setPayMode}>
                <SelectTrigger data-testid="pay-mode" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button data-testid="confirm-paid" onClick={markPaid} className="rounded-full">Confirm</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk WhatsApp reminder */}
      <Dialog open={reminderOpen} onOpenChange={setReminderOpen}>
        <DialogContent className="rounded-3xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="text-primary" size={20} /> Overdue Fee Reminders
            </DialogTitle>
          </DialogHeader>
          {loadingReminders && <div className="py-8 text-center text-muted-foreground">Finding overdue parents...</div>}
          {!loadingReminders && reminderData && (
            <div className="space-y-4">
              {reminderData.count === 0 ? (
                <div className="py-6 text-center">
                  <div className="text-4xl mb-2">🎉</div>
                  <div className="font-bold">No overdue fees. Every family is up to date!</div>
                </div>
              ) : (
                <>
                  <div className="rounded-2xl bg-muted/60 p-4 flex items-center gap-4">
                    <div className="flex-1">
                      <div className="text-xs uppercase font-bold text-muted-foreground tracking-widest">Total overdue</div>
                      <div className="text-2xl font-black">₹{Math.round(reminderData.total_amount).toLocaleString()}</div>
                      <div className="text-xs text-muted-foreground">{reminderData.count} {reminderData.count === 1 ? "family" : "families"}</div>
                    </div>
                    <Button data-testid="open-all-reminders" onClick={openAllReminders} className="rounded-full">
                      <Share2 size={14} /> Open all in WhatsApp
                    </Button>
                    <Button data-testid="email-all-reminders" onClick={emailAllReminders} variant="outline" className="rounded-full border-primary/40 text-primary">
                      <Mail size={14} /> Email all
                    </Button>
                  </div>

                  <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
                    {reminderData.recipients.map((r, i) => (
                      <div key={r.phone + i} data-testid={`reminder-${i}`} className="rounded-2xl border border-border p-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl chip-pink flex items-center justify-center font-black text-xs">
                            {r.child_name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-bold truncate">{r.parent_name} <span className="text-muted-foreground font-normal text-xs">· {r.child_name} · {r.class_name}</span></div>
                            <div className="text-xs text-muted-foreground">{r.phone} · {r.items} item{r.items > 1 ? "s" : ""} · ₹{Math.round(r.amount).toLocaleString()}</div>
                          </div>
                          <button
                            onClick={() => copyOne(i, r.message)}
                            className="p-2 rounded-xl hover:bg-muted text-muted-foreground"
                            title="Copy message"
                          >
                            {copiedIdx === i ? <Check size={14} className="text-primary" /> : <Copy size={14} />}
                          </button>
                          <a
                            href={r.wa_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`reminder-send-${i}`}
                            className="chip-green px-4 py-2 rounded-full text-xs font-bold hover:-translate-y-0.5 transition-all"
                          >
                            Send
                          </a>
                          {r.parent_email && (
                            <button
                              onClick={() => emailOne(r.child_id, r.child_name)}
                              data-testid={`reminder-email-${i}`}
                              className="chip-teal px-3 py-2 rounded-full text-xs font-bold hover:-translate-y-0.5 transition-all flex items-center gap-1"
                              title={`Email ${r.parent_email}`}
                            >
                              <Mail size={12} /> Email
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
